import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/feature/SeoHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { articleImages } from '@/mocks/articleImages';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCat, setActiveCat] = useState(slug || categories[0].slug);

  useEffect(() => { window.scrollTo(0, 0); }, [activeCat]);
  useEffect(() => { if (slug) setActiveCat(slug); }, [slug]);

  const category = categories.find((c) => c.slug === activeCat) || categories[0];
  const categoryArticles = articles.filter((a) => a.categorySlug === activeCat);

  const seoKeywords = `就去色色,${category.name},色彩搭配灵感,视觉美学设计,创意配色方案`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 就去色色`,
    description: category.description,
    url: `https://jiuqu.sese/category/${activeCat}`,
  };

  return (
    <>
      <SeoHead title={`${category.name}`} description={category.description} keywords={seoKeywords} canonicalUrl={`/category/${activeCat}`} jsonLd={jsonLd} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-14 md:pb-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' },{ label: category.name }]} />

            <div className="mt-6 mb-10">
              <h1 className="text-2xl md:text-4xl font-heading text-foreground-900">{category.name}</h1>
              <p className="mt-3 text-sm md:text-base text-foreground-500 max-w-2xl">{category.description}</p>
              <div className="mt-4 flex items-center gap-1.5">
                <i className={`${category.icon} text-primary-500`}></i>
                <span className="text-sm text-foreground-400">{categoryArticles.length} 篇文章</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-10">
              {categories.map((cat) => (
                <Link key={cat.id} to={`/category/${cat.slug}`} onClick={() => setActiveCat(cat.slug)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${activeCat === cat.slug ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}>{cat.name}</Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => {
                  const imgSrc = articleImages[article.slug] || '';
                  return (
                    <Link key={article.id} to={`/article/${article.slug}`} className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-300/50 transition-all duration-300">
                      <div className="relative w-full h-48 overflow-hidden">
                        {imgSrc && <img src={imgSrc} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />}
                      </div>
                      <div className="p-5">
                        <h3 className="text-base font-heading text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">{article.title}</h3>
                        <p className="mt-2 text-sm text-foreground-500 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                          {article.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-full whitespace-nowrap">{tag}</span>
                          ))}
                        </div>
                        <div className="mt-3 flex items-center justify-between text-xs text-foreground-400">
                          <span>{article.date}</span>
                          <span>{article.readTime} 分钟阅读</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300"></i>
                <p className="mt-4 text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}